import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

export const LEAD_STATUSES = [
  "New Lead",
  "Contacted",
  "Follow Up",
  "Interested",
  "Quotation Sent",
  "Won",
  "Lost",
  "Hot",
  "Reminder Follow-up",
];

export const LEAD_STATUS_COLORS = {
  "New Lead": "default",
  Contacted: "info",
  "Follow Up": "secondary",
  Interested: "primary",
  "Quotation Sent": "warning",
  Won: "success",
  Lost: "error",
  Hot: "error",
  "Reminder Follow-up": "info",
};

const initialState = {
  list: [],
  perPage: "",
  total: 0,
  loading: false,
  detail: null,
  detailLoading: false,
  detailFetched: false,
  messages: [],
  messagesLoading: false,
  activities: [],
  activitiesLoading: false,
  assignableUsers: [],
  sampleProducts: [],
  sampleProductsLoading: false,
};

export const getLeadList = createAsyncThunk(
  "get-lead",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("lead/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const createLead = createAsyncThunk(
  "create-lead",
  async (data, { dispatch }) => {
    try {
      const response = await axios.post("lead", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const editLead = createAsyncThunk(
  "edit-lead",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`lead/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const updateLead = createAsyncThunk(
  "update-lead",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.put(`/lead/${id}`, data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const deleteLead = createAsyncThunk(
  "delete-lead",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`/lead/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getLeadMessages = createAsyncThunk(
  "get-lead-messages",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`/lead/${id}/messages`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const addLeadNote = createAsyncThunk(
  "add-lead-note",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.post(`/lead/${id}/notes`, data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getLeadActivities = createAsyncThunk(
  "get-lead-activities",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`/lead/${id}/activities`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const uploadLeadImages = createAsyncThunk(
  "upload-lead-images",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.post(`/lead/${id}/images`, data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const deleteLeadImage = createAsyncThunk(
  "delete-lead-image",
  async ({ id, filename }, { dispatch }) => {
    try {
      const response = await axios.delete(
        `/lead/${id}/images/${encodeURIComponent(filename)}`
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getAssignableUsers = createAsyncThunk(
  "get-lead-assignable-users",
  async (_, { dispatch }) => {
    try {
      const response = await axios.get("/lead/assignable-users");
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const importLeads = createAsyncThunk(
  "import-leads",
  async (data, { dispatch }) => {
    try {
      const response = await axios.post("/lead/import", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getLeadSampleProducts = createAsyncThunk(
  "get-lead-sample-products",
  async (_, { dispatch }) => {
    try {
      const response = await axios.get("/lead/sample-products");
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

const leadSlice = createSlice({
  name: "lead",
  initialState,
  reducers: {
    clearLeadMessages: (state) => {
      state.messages = [];
    },
    clearLeadDetail: (state) => {
      state.detail = null;
      state.detailFetched = false;
      state.activities = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLeadList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLeadList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action?.payload?.data?.data?.leadList ?? [];
        state.perPage = action?.payload?.data?.data?.perPage ?? 1;
        state.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getLeadList.rejected, (state) => {
        state.loading = false;
        state.list = [];
        state.perPage = "";
        state.total = 0;
      })
      .addCase(editLead.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(editLead.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detailFetched = true;
        state.detail = action?.payload?.data?.data?.leadDetail ?? null;
      })
      .addCase(editLead.rejected, (state) => {
        state.detailLoading = false;
        state.detailFetched = true;
        state.detail = null;
      })
      .addCase(getLeadMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(getLeadMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.messages = action?.payload?.data?.data?.messageList ?? [];
      })
      .addCase(getLeadMessages.rejected, (state) => {
        state.messagesLoading = false;
        state.messages = [];
      })
      .addCase(getLeadActivities.pending, (state) => {
        state.activitiesLoading = true;
      })
      .addCase(getLeadActivities.fulfilled, (state, action) => {
        state.activitiesLoading = false;
        state.activities = action?.payload?.data?.data?.activityList ?? [];
      })
      .addCase(getLeadActivities.rejected, (state) => {
        state.activitiesLoading = false;
        state.activities = [];
      })
      .addCase(getAssignableUsers.fulfilled, (state, action) => {
        state.assignableUsers = action?.payload?.data?.data?.userList ?? [];
      })
      .addCase(getAssignableUsers.rejected, (state) => {
        state.assignableUsers = [];
      })
      .addCase(getLeadSampleProducts.pending, (state) => {
        state.sampleProductsLoading = true;
      })
      .addCase(getLeadSampleProducts.fulfilled, (state, action) => {
        state.sampleProductsLoading = false;
        state.sampleProducts = action?.payload?.data?.data?.productList ?? [];
      })
      .addCase(getLeadSampleProducts.rejected, (state) => {
        state.sampleProductsLoading = false;
        state.sampleProducts = [];
      });
  },
});

export const { clearLeadMessages, clearLeadDetail } = leadSlice.actions;
export default leadSlice.reducer;
